import { queryOptions } from "@tanstack/react-query"
import { cfApi } from "./client"
import type { DealDetails, PublicDealDetails } from "./deals-types"
import { dealsApi } from "./deals-api"

export type {
  DealDetails,
  GetDealDetailsParams,
  ProcessingRequest,
  PublicDealDetails,
} from "./deals-types"
export * from "./deals-api"

// Query keys factory
export const dealsQueryKeys = {
  deals: ["cf", "deals"] as const,
  dealDetails: (dealId: string) => ["cf", "deals", "detail", dealId] as const,
  publicDealDetails: (dealId: string) =>
    ["cf", "deals", "public", dealId] as const,
} as const

/**
 * Query options factory for fetching deal details
 * Returns a queryOptions object that can be used with useQuery or useSuspenseQuery
 */
export function dealDetailsQueryOptions(dealId: string) {
  return queryOptions({
    queryKey: dealsQueryKeys.dealDetails(dealId),
    queryFn: async () => {
      return await cfApi.get<DealDetails>(`/api/deals/${dealId}`)
    },
    enabled: !!dealId,
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    gcTime: 0, // Don't cache - always fetch fresh data
  })
}

/**
 * Query options factory for fetching public deal details (no auth required)
 * Returns a queryOptions object that can be used with useQuery or useSuspenseQuery
 */
export function publicDealDetailsQueryOptions(dealId: string) {
  return queryOptions({
    queryKey: dealsQueryKeys.publicDealDetails(dealId),
    queryFn: async () => {
      return await cfApi.get<PublicDealDetails>(`/api/public/deals/${dealId}`)
    },
    enabled: !!dealId,
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    gcTime: 0, // Don't cache - always fetch fresh data
  })
}

/**
 * Query options factory for fetching deal list for a user
 * Returns a queryOptions object that can be used with useQuery or useSuspenseQuery
 */
export function dealListQueryOptions() {
  return queryOptions({
    queryKey: dealsQueryKeys.deals,
    queryFn: async () => {
      const resp = await dealsApi.list()
      return resp.results.map((deal) => {
        return {
          createdAt: deal.createdAt,
          updatedAt: deal.updatedAt,
          id: deal.id,
          ...deal.properties,
          amount: deal.properties.loan_amount
            ? parseFloat(deal.properties.loan_amount)
            : null,
        }
      })
    },
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache but don't persist
  })
}

export default dealsApi
